import React, { useMemo } from 'react';
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { ClearEditorPlugin } from '@lexical/react/LexicalClearEditorPlugin';
import { type InitialConfigType, LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import LexicalErrorBoundary from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin';
import type { EditorState, LexicalEditor } from 'lexical';
import {
  createBeautifulMentionNode,
  ZeroWidthNode,
  ZeroWidthPlugin,
} from 'lexical-beautiful-mentions';
import { twMerge } from 'tailwind-merge';
import type { CustomMentionNode } from '@soundxyz/utils/lexical/ContentLexicalStateV1';
import { MentionNode } from '@components/Lexical/Nodes/Mention';
import { CharacterCountPlugin } from '@components/Lexical/Plugins/CharacterCountPlugin';
import { DisabledPlugin } from '@components/Lexical/Plugins/DisabledPlugin';
import { EnterHitPlugin } from '@components/Lexical/Plugins/EnterHitPlugin';
import { FocusContainerPlugin } from '@components/Lexical/Plugins/FocusContainerPlugin';
import { MentionsPlugin } from '@components/Lexical/Plugins/Mentions';
import { SendButtonPlugin } from '@components/Lexical/Plugins/SendButtonPlugin';
import { SingleLinePlugin } from '@components/Lexical/Plugins/SingleLinePlugin';
import { logger } from '@utils/logger';

// Catch any errors that occur during Lexical updates and log them
// or throw them as needed. If you don't throw them, Lexical will
// try to recover gracefully without losing user data.
function onError(error: Error) {
  logger.error({ err: error }, 'LexicalInput Error');
}

interface SingleLineProps {
  loading: boolean;
  onSubmit: (editorState: EditorState) => void;
  type: 'single-line';
}

interface MultiLineProps {
  type: 'multi-line';
}

interface BaseProps {
  autoFocus: boolean;
  className: string;
  disabled: boolean;
  initialMentionData: CustomMentionNode['data'] | undefined;
  /**
   * Used to style the menu, often for z-index logic
   */
  menuAnchorClassName?: string;
  /*
   * ID to be used to focus component externally using querySelector
   */
  id: string | undefined;
  onChange: ((editorState: EditorState, editor: LexicalEditor, tags: Set<string>) => void) | null;
  placeholder: string;
  placeholderClassName?: string;
  characterCount:
    | {
        show: boolean;
        maxLength: number;
      }
    | false;
}

type Props = BaseProps & (SingleLineProps | MultiLineProps);

export function LexicalInput(props: Props) {
  const {
    autoFocus,
    className,
    disabled,
    id,
    onChange,
    initialMentionData,
    placeholder,
    placeholderClassName,
    characterCount,
    type,
    menuAnchorClassName,
  } = props;

  const initialConfig: InitialConfigType = useMemo(
    () => ({
      namespace: 'MentionableInput',
      onError,
      nodes: [...createBeautifulMentionNode(MentionNode), ZeroWidthNode],
    }),
    [],
  );

  const isSingleLine = type === 'single-line';

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <FocusContainerPlugin
        className={twMerge(
          'relative cursor-text overflow-hidden rounded-md bg-neutral100 p-2 outline-black focus-within:outline focus-within:outline-2',
          isSingleLine && 'pr-10',

          className,
        )}
      >
        <div
          className={twMerge(
            'h-full w-full overflow-y-auto',
            isSingleLine ? 'scrollbar-none' : 'scrollbar-light',
          )}
        >
          <PlainTextPlugin
            contentEditable={
              <ContentEditable
                id={id}
                className={twMerge(`outline-none`, isSingleLine ? '!whitespace-pre' : 'pb-8')}
              />
            }
            placeholder={
              <div
                className={twMerge(
                  'action pointer-events-none absolute left-0 right-0 top-0 line-clamp-1 select-none p-2 text-neutral400',
                  isSingleLine && 'pr-10',
                  placeholderClassName,
                )}
              >
                {placeholder}
              </div>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          <HistoryPlugin />
          {onChange && <OnChangePlugin onChange={onChange} />}

          <ZeroWidthPlugin />
          <ClearEditorPlugin />
          <DisabledPlugin disabled={disabled} />

          {/**
           * Conditional plugins
           */}
          {isSingleLine && (
            <>
              <SingleLinePlugin />
              <EnterHitPlugin onHit={props.onSubmit} />
            </>
          )}
          <MentionsPlugin
            initialMentionData={initialMentionData}
            menuAnchorClassName={menuAnchorClassName}
          />
          {autoFocus && <AutoFocusPlugin />}
        </div>
        {!!characterCount && characterCount.show && (
          <CharacterCountPlugin maxLength={characterCount.maxLength} />
        )}
        {isSingleLine && <SendButtonPlugin loading={props.loading} onSubmit={props.onSubmit} />}
      </FocusContainerPlugin>
    </LexicalComposer>
  );
}
